import { useEffect, useId, useState, type ChangeEvent } from 'react';
import type { CustomSymbol } from '@shared/schemas';
import type { LocalizedText } from '@shared/symbols';
import { LIMITS } from '@shared/constants';
import { useT } from '../i18n';
import { compressImage, ImageError } from '../lib/image';
import { newId } from '../lib/ids';
import { Dialog } from './Dialog';
import { EmojiPicker } from './EmojiPicker';
import { Notice } from './Notice';
import { Pictogram } from './Pictogram';

interface Props {
  open: boolean;
  categoryId: string;
  categoryName: string;
  /** Symbol being edited; null to create a new one. */
  symbol: CustomSymbol | null;
  onClose(): void;
  onSave(symbol: CustomSymbol): void;
  /**
   * Editing a built-in symbol (e.g. "Mamãe"): its original name and picture.
   * The first tab then shows the original picture instead of the emoji list.
   */
  original?: { text: LocalizedText; icon: string; image: string };
  /** Shown for a built-in symbol that was already changed: goes back to the original. */
  onRestore?(): void;
}

export function SymbolEditor({ open, categoryId, categoryName, symbol, onClose, onSave, original, onRestore }: Props) {
  const t = useT();
  const ptId = useId();
  const enId = useId();
  const cameraId = useId();
  const galleryId = useId();
  const [pt, setPt] = useState('');
  const [en, setEn] = useState('');
  const [icon, setIcon] = useState('💬');
  const [image, setImage] = useState<string | null>(null);
  const [tab, setTab] = useState<'emoji' | 'photo'>('emoji');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setPt(symbol?.text.pt ?? '');
    // English is optional: when it equals the Portuguese word, show it empty.
    setEn(symbol && symbol.text.en !== symbol.text.pt ? symbol.text.en : '');
    setIcon(symbol?.icon ?? '💬');
    setImage(symbol?.image ?? null);
    setTab(symbol?.image ? 'photo' : 'emoji');
    setError(null);
    setBusy(false);
  }, [open, symbol]);

  async function handleFile(e: ChangeEvent<HTMLInputElement>) {
    const input = e.currentTarget;
    const file = input.files?.[0];
    // Clear right away so choosing the same photo again still fires "change".
    input.value = '';
    if (!file) return;
    setBusy(true);
    setError(null);
    try {
      setImage(await compressImage(file));
    } catch (err) {
      setError(err instanceof ImageError ? err.message : t('imageFailed'));
    } finally {
      setBusy(false);
    }
  }

  function submit() {
    const ptWord = pt.trim() || en.trim();
    if (!ptWord) {
      setError(t('wordRequired'));
      return;
    }
    onSave({
      id: symbol?.id ?? newId('sym'),
      categoryId,
      category: categoryName,
      text: { pt: ptWord, en: en.trim() || (original && ptWord === original.text.pt ? original.text.en : ptWord) },
      icon: original ? original.icon : icon,
      image: tab === 'photo' ? image : null,
    });
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={symbol ? t('editSymbolTitle') : t('newSymbolTitle')}
      footer={
        <>
          {onRestore && (
            <button type="button" className="btn btn--ghost push-left" onClick={onRestore}>
              ↺ {t('restoreOriginal')}
            </button>
          )}
          <button type="button" className="btn btn--ghost" onClick={onClose}>
            {t('cancel')}
          </button>
          <button type="button" className="btn btn--primary" onClick={submit} disabled={busy}>
            {t('save')}
          </button>
        </>
      }
    >
      <form
        className="form"
        onSubmit={(e) => {
          e.preventDefault();
          submit();
        }}
      >
        <p className="muted">{t('symbolInCategory', { name: categoryName })}</p>
        <div className="field">
          <label htmlFor={ptId}>{t('wordPt')}</label>
          <input id={ptId} value={pt} maxLength={LIMITS.maxLabelLength} onChange={(e) => setPt(e.target.value)} autoFocus />
        </div>
        <div className="field">
          <label htmlFor={enId}>{t('wordEn')}</label>
          <input id={enId} value={en} maxLength={LIMITS.maxLabelLength} onChange={(e) => setEn(e.target.value)} />
        </div>

        <div className="field">
          <span className="field__label">{t('chooseImage')}</span>
          <div className="segmented" role="tablist">
            <button type="button" role="tab" aria-selected={tab === 'emoji'} onClick={() => setTab('emoji')}>
              {original ? `🖼️ ${t('originalTab')}` : `😀 ${t('emojiTab')}`}
            </button>
            <button type="button" role="tab" aria-selected={tab === 'photo'} onClick={() => setTab('photo')}>
              📷 {t('photoTab')}
            </button>
          </div>
        </div>

        {tab === 'emoji' && original ? (
          <div className="photo-picker__preview">
            <Pictogram source={{ icon: original.icon, image: original.image }} className="original-picto" />
          </div>
        ) : tab === 'emoji' ? (
          <EmojiPicker value={icon} onChange={setIcon} label={t('emojiTab')} />
        ) : (
          <div className="photo-picker">
            {image ? (
              <div className="photo-picker__preview">
                <img src={image} alt="" />
                <button type="button" className="btn btn--ghost" onClick={() => setImage(null)}>
                  {t('removePhoto')}
                </button>
              </div>
            ) : busy ? (
              <p className="photo-picker__busy">{t('processingImage')}</p>
            ) : (
              /*
               * Two native inputs opened by their <label>s (no script-triggered click):
               * "capture" opens the camera directly on phones and tablets, while the
               * other opens the gallery/files. A single input without "capture" left the
               * camera hidden behind a chooser on some Android tablets (Galaxy Tab S6).
               */
              <div className="photo-picker__options">
                <label htmlFor={cameraId} className="photo-picker__option">
                  <span aria-hidden="true">📷</span> {t('takePhoto')}
                </label>
                <label htmlFor={galleryId} className="photo-picker__option">
                  <span aria-hidden="true">🖼️</span> {t('chooseFromGallery')}
                </label>
              </div>
            )}
            <input
              id={cameraId}
              type="file"
              accept="image/*"
              capture="environment"
              className="sr-only"
              onChange={(e) => void handleFile(e)}
            />
            <input
              id={galleryId}
              type="file"
              accept="image/*"
              className="sr-only"
              onChange={(e) => void handleFile(e)}
            />
          </div>
        )}
        {error && <Notice kind="error">{error}</Notice>}
      </form>
    </Dialog>
  );
}
